import { useEffect, useState, type ReactNode } from "react";
import { Alert, App, Button, Collapse, Modal, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import type { InviteBulkDto } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useUserMutations } from "../../api/admin";
import { useModalProps } from "../useModalProps";
import { Sensitive } from "../Sensitive";

/** Invito massivo dei referenti fornitori: anteprima (nuovi, già utenti, senza email) e conferma. */
export function BulkInviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const modalProps = useModalProps(640);
  const m = useUserMutations();
  const [preview, setPreview] = useState<InviteBulkDto | null>(null);
  const [done, setDone] = useState<InviteBulkDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setDone(null);
    setError(null);
    m.inviteBulk.mutateAsync({ dryRun: true }).then(setPreview, (e) => setError(e instanceof ApiError ? e.message : t("a_loadFail")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = async () => {
    try {
      const r = await m.inviteBulk.mutateAsync({ dryRun: false });
      setDone(r);
      void message.success(t("c_bulkDone", { n: r.created.length }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
  };

  const view = done ?? preview;
  const n = preview?.newUsers.length ?? 0;
  const list = (rows: { key: string; a: string; b: ReactNode; tag?: string }[]) => (
    <div style={{ maxHeight: 240, overflowY: "auto", fontSize: 13 }}>
      {rows.map((r) => (
        <div key={r.key} style={{ padding: "3px 0", overflowWrap: "anywhere" }}>
          <b>{r.a}</b> · {r.b} {r.tag && <Tag>{r.tag}</Tag>}
        </div>
      ))}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("c_bulkTitle")}
      {...modalProps}
      destroyOnHidden
      footer={
        done ? (
          <Button type="primary" onClick={onClose}>{t("close")}</Button>
        ) : (
          <Space>
            <Button onClick={onClose}>{t("cancel")}</Button>
            <Button type="primary" disabled={!n} loading={m.inviteBulk.isPending && !!preview} onClick={confirm}>{t("c_bulkConfirm", { n })}</Button>
          </Space>
        )
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        <Typography.Text type="secondary">{t("c_bulkHelp")}</Typography.Text>
        {error && <Alert type="error" showIcon message={error} />}
        {!view && !error && <Typography.Text type="secondary">…</Typography.Text>}
        {view && (
          <>
            {!view.newUsers.length && !done && <Alert type="info" showIcon message={t("c_bulkNone")} />}
            {done && <Alert type="success" showIcon message={t("c_bulkDone", { n: done.created.length })} description={t("c_bulkMailNote")} />}
            <Collapse
              defaultActiveKey={["new"]}
              items={[
                {
                  key: "new",
                  label: `${t("c_bulkNew")} (${view.newUsers.length})`,
                  children: list(view.newUsers.map((r) => ({ key: r.supplierId, a: r.supplierName, b: <Sensitive>{`${r.name} <${r.email}>`}</Sensitive> }))),
                },
                {
                  key: "ex",
                  label: `${t("c_bulkExisting")} (${view.existing.length})`,
                  children: list(view.existing.map((r) => ({ key: r.supplierId, a: r.supplierName, b: <Sensitive>{r.email}</Sensitive>, tag: r.reason === "duplicate_in_list" ? t("c_bulkDuplicate") : undefined }))),
                },
                {
                  key: "no",
                  label: `${t("c_bulkNoEmail")} (${view.noEmail.length})`,
                  children: list(view.noEmail.map((r) => ({ key: r.supplierId, a: r.supplierName, b: <Sensitive>{r.email || "—"}</Sensitive> }))),
                },
              ]}
            />
          </>
        )}
      </Space>
    </Modal>
  );
}
